import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, ShieldCheck, ArrowRight, Copy, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/lib/api";
import LegalDisclaimer from "@/components/LegalDisclaimer";

export default function Verify() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    if (!certId) { setState({ loading: false, data: null, error: null }); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/verify/${certId}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setState({ loading: false, data, error: null });
      } catch {
        setState({ loading: false, data: null, error: "Could not verify certificate" });
      }
    })();
  }, [certId]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = searchQ.trim();
    if (q.length < 2) return toast.error("Enter at least 2 characters");
    setSearching(true);
    try {
      const res = await fetch(`${API}/verify/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setSearchResults(data);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const { loading, data } = state;
  const found = data?.found;
  const valid = data?.valid;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="verify-logo">
            <img src="/imcac_logo.png" alt="IMCAC" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-sm tracking-tight">
              IMCAC<span className="text-red-800">.</span>
              <span className="hidden sm:inline text-zinc-500 font-normal ml-1">
                Department of Occupational Safety Programs
              </span>
            </span>
          </Link>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            Certificate Verification
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
          Certificate lookup
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
          Certificate lookup
        </h1>
        <p className="text-zinc-600 mt-3 max-w-xl">
          This page confirms that a certificate was <strong>issued by IMCAC</strong> and shows its
          current status. It is a lookup for an independent competency credential — <strong>not</strong>{" "}
          a regulator, accreditation, or licensing check.
        </p>

        <div className="mt-6">
          <LegalDisclaimer />
        </div>

        {loading && certId && (
          <div className="mt-10 text-sm text-zinc-500 font-mono" data-testid="verify-loading">
            Looking up certificate…
          </div>
        )}

        {/* Name search (always visible) */}
        <div className="mt-10 bg-white border border-zinc-200 rounded-lg p-6" data-testid="verify-search-panel">
          <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
            Search by holder name
          </div>
          <form onSubmit={runSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="e.g. Jane Doe"
                className="pl-9 h-11"
                data-testid="verify-search-input"
              />
            </div>
            <Button type="submit" disabled={searching} className="bg-red-600 hover:bg-red-700 text-white h-11 px-5" data-testid="verify-search-btn">
              {searching ? "Searching…" : "Search"}
            </Button>
          </form>

          {searchResults && (
            <div className="mt-5" data-testid="verify-search-results">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                {searchResults.count} result{searchResults.count === 1 ? "" : "s"} for &ldquo;{searchResults.query}&rdquo;
              </div>
              {searchResults.count === 0 ? (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-6 text-center">
                  No paid, issued certificates match that name.
                </div>
              ) : (
                <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-md" data-testid="verify-search-list">
                  {searchResults.results.map((r) => (
                    <Link
                      key={r.certificate_id}
                      to={`/verify/${r.certificate_id}`}
                      className="flex items-center justify-between gap-4 py-3 px-4 hover:bg-zinc-50 group"
                      data-testid={`verify-search-item-${r.certificate_id}`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-3">
                        <User className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.holder_name}</div>
                          <div className="text-xs text-zinc-500 font-mono truncate">{r.certificate_id}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="font-mono text-sm font-bold">{r.score}%</div>
                          <div className="text-[10px] font-mono text-zinc-500">
                            {String(r.issued_at).slice(0, 10)}
                          </div>
                        </div>
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${r.valid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {r.valid ? "Valid" : "Expired"}
                        </span>
                        <ArrowRight className="h-4 w-4 text-zinc-400 group-hover:text-red-600" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {!loading && !found && certId && (
          <div
            className="mt-10 border-2 border-red-600 bg-red-50 rounded-lg p-8"
            data-testid="verify-not-found"
          >
            <XCircle className="h-10 w-10 text-red-600 mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-bold text-red-900">
              Certificate not found
            </h2>
            <p className="text-red-800 mt-2 text-sm">
              We could not locate a certificate with ID{" "}
              <span className="font-mono">{certId}</span>. Check the ID and try again, or ask the
              holder to re-send the certificate PDF.
            </p>
          </div>
        )}

        {!loading && found && (
          <div
            className={`mt-10 rounded-lg p-8 border-2 ${
              valid ? "bg-white border-emerald-600" : "bg-white border-amber-600"
            }`}
            data-testid="verify-result"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                {valid ? (
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest" data-testid="verify-status">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Valid
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest" data-testid="verify-status">
                    <XCircle className="h-3.5 w-3.5" /> Expired
                  </div>
                )}
                <h2 className="font-display text-3xl font-bold tracking-tight mt-4" data-testid="verify-holder-name">
                  {data.holder_name}
                </h2>
                <p className="text-zinc-600 mt-1">holder of certificate</p>
                <p className="font-mono text-sm text-zinc-700 mt-1 flex items-center gap-2">
                  <span data-testid="verify-cert-id">{data.certificate_id}</span>
                  <button
                    className="text-zinc-500 hover:text-red-600"
                    onClick={() => {
                      navigator.clipboard.writeText(data.certificate_id);
                      toast.success("Certificate ID copied");
                    }}
                    data-testid="verify-copy-id"
                    aria-label="Copy certificate ID"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </p>
              </div>
              <ShieldCheck
                className={`h-14 w-14 ${valid ? "text-emerald-600" : "text-amber-600"}`}
                strokeWidth={1.2}
              />
            </div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border-t border-l border-zinc-200">
              <div className="border-r border-b border-zinc-200 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Score
                </div>
                <div className="font-display font-bold text-2xl text-red-600 mt-1" data-testid="verify-score">
                  {data.score}%
                </div>
              </div>
              <div className="border-r border-b border-zinc-200 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Issued
                </div>
                <div className="font-mono text-sm mt-1" data-testid="verify-issued">
                  {String(data.issued_at).slice(0, 10)}
                </div>
              </div>
              <div className="border-r border-b border-zinc-200 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Valid until
                </div>
                <div className="font-mono text-sm mt-1" data-testid="verify-expires">
                  {String(data.expires_at).slice(0, 10)}
                </div>
              </div>
              <div className="border-r border-b border-zinc-200 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Status
                </div>
                <div className={`font-mono text-sm mt-1 ${valid ? "text-emerald-700" : "text-amber-700"}`}>
                  {valid ? "Active" : "Expired"}
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-zinc-200 pt-5 text-sm text-zinc-700">
              <span className="font-semibold">Course:</span> {data.course}
            </div>
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-3">
          <Button asChild className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="verify-cta-training">
            <Link to="/">
              About the training <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" asChild data-testid="verify-cta-cert">
            <Link to="/register">Get your own certificate</Link>
          </Button>
        </div>

        <p className="text-xs text-zinc-500 mt-10 max-w-xl">
          Competency certificates issued by IMCAC document successful completion of a
          competency-based assessment aligned to OSHA 29 CFR 1910.1030 topics, and are valid for
          two calendar years from the date of issue. Validity status shown here is live.
          IMCAC/DOOSP certification does not replace professional registration or licensure imposed
          by any national or professional authority.
        </p>
      </main>

      <footer className="mt-12 bg-zinc-900 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <LegalDisclaimer variant="footer" />
        </div>
      </footer>
    </div>
  );
}
